import { useEffect, useState } from 'react';
import { Modal, Space, Tabs } from 'antd';
import { WebApiModelsEmailTemplate, WebApiServicesDataTransferObjectsEmailServiceOneFrontEmailTemplateDto } from '../../Repository/eventbookyapi';
import { useAuth } from '../../Contexts/AuthContext';
import { SubscriptionLevel } from '../../Types/SubscriptionLevel';
import { TextService } from '../../Services/TextService';
import { DeleteOutlined, ExclamationCircleFilled } from '@ant-design/icons';
import Table, { ColumnsType } from 'antd/lib/table';
import Paragraph from 'antd/lib/typography/Paragraph';
import LeftMenuSettings from './LeftSettings';
import { EmailTemplateService } from '../../Services/EmailTemplateService';
import CreateEmailTemplateButton from './CreateEmailTemplateButton';
import EditEmailTemplate from './EditEmailTemplate';
import LayoutLeftMenu from '../../Components/Layout/LayoutLeftMenu';
import { IntegrationService } from '../../Services/IntegrationService';
import { IntegrationType } from '../../Types/IntegrationType';
import { mapPurposeToText } from '../../Mappers/mapAvailablePurpose';
import OneFrontTemplateGallery from './OneFrontTemplateGallery';
import { BookyButton } from '../../Components/Buttons/BookyButton';

const AdminEmailTemplatesAndAutomation = () => {
    const [templates, setTemplates] = useState<WebApiModelsEmailTemplate[]>([]);
    const [automationTemplates, setAutomationTemplates] = useState<WebApiModelsEmailTemplate[]>([]);
    const { user } = useAuth();
    const GetText = TextService.GetText;
    const { confirm } = Modal;
    const [enablePaymentLink, setEnablePaymentLink] = useState(false);
    const [prefillTemplate, setPrefillTemplate] = useState<WebApiServicesDataTransferObjectsEmailServiceOneFrontEmailTemplateDto>();
    // Each tab has its own create modal, so each gets its own request to open prefilled.
    const [prefillRequestId, setPrefillRequestId] = useState(0);
    const [automationPrefillRequestId, setAutomationPrefillRequestId] = useState(0);
    const [blankRequestId, setBlankRequestId] = useState(0);
    const [automationBlankRequestId, setAutomationBlankRequestId] = useState(0);
    const [isGalleryOpen, setIsGalleryOpen] = useState(false);
    const [isAutomationGalleryOpen, setIsAutomationGalleryOpen] = useState(false);
    const reachedFreeTemplateLimit = user?.subscriptionLevel === SubscriptionLevel.Free && templates.length >= 1;

    const onUseOneFrontTemplate = (template: WebApiServicesDataTransferObjectsEmailServiceOneFrontEmailTemplateDto) => {
        setPrefillTemplate(template);
        setPrefillRequestId(previous => previous + 1);
    };

    const onUseOneFrontAutomationTemplate = (template: WebApiServicesDataTransferObjectsEmailServiceOneFrontEmailTemplateDto) => {
        setPrefillTemplate(template);
        setAutomationPrefillRequestId(previous => previous + 1);
    };

    useEffect(() => {
        IntegrationService.GetActiveIntegrations().then(result => {
            setEnablePaymentLink(!!result.find(p => p.type === IntegrationType.PingPayments));
        });
        reloadTemplates();
    }, []);

    const deleteTemplate = async (id: number) => {
        confirm({
            title: GetText('EmailTemplates_DeleteConfirm'),
            icon: <ExclamationCircleFilled />,
            content: GetText('EmailTemplates_DeleteConfirmMessage'),
            async onOk() {
                await EmailTemplateService.DeleteTemplate(id);
                reloadTemplates();
            },
            onCancel() {},
        });
    };

    const reloadTemplates = () => {
        EmailTemplateService.GetTemplates().then(result => {
            setTemplates(result.filter(t => !t.purpose));
            setAutomationTemplates(result.filter(t => !!t.purpose));
        });
    };

    const templateColumns: ColumnsType<WebApiModelsEmailTemplate> = [
        {
            title: GetText('Group_NameColumn'),
            dataIndex: 'uniqueId',
            key: 'uniqueId',
            render: (_, template) => <span><b>{template.templateName}</b></span>,
        },
        {
            title: GetText('MemberList_ActionColumn'),
            key: 'action',
            render: (_, template) => (
                <Space size="middle">
                    <EditEmailTemplate enablePaymentLink={enablePaymentLink} reloadTemplate={reloadTemplates} templates={templates} template={template}>Edit</EditEmailTemplate>
                    <DeleteOutlined style={{ color: 'red', fontSize: '20px' }} onClick={() => deleteTemplate(template.id!)} />
                </Space>
            ),
        },
    ];

    const automationColumns: ColumnsType<WebApiModelsEmailTemplate> = [
        {
            title: GetText('Group_NameColumn'),
            dataIndex: 'uniqueId',
            key: 'uniqueId',
            render: (_, template) => <span><b>{template.templateName}</b></span>,
        },
        {
            title: GetText('AutomaticSending_EventColumn'),
            dataIndex: 'purpose',
            key: 'purpose',
            render: (_, template) => <span>{mapPurposeToText(template.purpose)}</span>,
        },
        {
            title: GetText('MemberList_ActionColumn'),
            key: 'action',
            render: (_, template) => (
                <Space size="middle">
                    <EditEmailTemplate isAutomation enablePaymentLink={enablePaymentLink} reloadTemplate={reloadTemplates} templates={automationTemplates} template={template}>Edit</EditEmailTemplate>
                    <DeleteOutlined style={{ color: 'red', fontSize: '20px' }} onClick={() => deleteTemplate(template.id!)} />
                </Space>
            ),
        },
    ];

    const tabItems = [
        {
            key: 'templates',
            label: GetText('EmailTemplates_TemplateTitle'),
            children: (
                <>
                    <Paragraph>{GetText('EmailTemplates_Description')}</Paragraph>
                    {reachedFreeTemplateLimit
                        ? <i><a href='/Pricing'>{GetText('Common_UpgradeToPremium')}</a> {GetText('EmailTemplates_UpgradeTo2')}</i>
                        : <>
                            {/* One button opens the chooser: an empty template or a standard template. */}
                            <BookyButton type="primary" fullWidth={false} text={GetText('CreateTemplate_CreateText')} onClick={() => setIsGalleryOpen(true)} />
                            <CreateEmailTemplateButton hideButton hidePurpose enablePaymentLink={enablePaymentLink} user={user} reloadTemplate={reloadTemplates}
                                prefillTemplate={prefillTemplate} prefillRequestId={prefillRequestId} openBlankRequestId={blankRequestId} />
                        </>
                    }
                    <Table columns={templateColumns} style={{ marginTop: '20px' }} locale={{ emptyText: GetText('EmailTemplates_NoGroupsCreated') }} className="admin-table" rowKey="Id" dataSource={templates} />
                    <OneFrontTemplateGallery open={isGalleryOpen} title={GetText('CreateTemplate_ModalHeader')} enablePaymentLink={enablePaymentLink}
                        onClose={() => setIsGalleryOpen(false)} onCreateBlank={() => setBlankRequestId(previous => previous + 1)} onUseTemplate={onUseOneFrontTemplate} />
                </>
            ),
        },
        {
            key: 'automation',
            label: GetText('AutomaticSending_Title'),
            children: (
                <>
                    <Paragraph>{GetText('AutomaticSending_Description')}</Paragraph>
                    <BookyButton type="primary" fullWidth={false} text={GetText('AutomaticSending_CreateModalTitle')} onClick={() => setIsAutomationGalleryOpen(true)} />
                    <CreateEmailTemplateButton hideButton isAutomation enablePaymentLink={enablePaymentLink} user={user} reloadTemplate={reloadTemplates}
                        prefillTemplate={prefillTemplate} prefillRequestId={automationPrefillRequestId} openBlankRequestId={automationBlankRequestId} />
                    <Table columns={automationColumns} style={{ marginTop: '20px' }} locale={{ emptyText: GetText('AutomaticSending_NoItems') }} className="admin-table" rowKey="Id" dataSource={automationTemplates} />
                    <OneFrontTemplateGallery automation open={isAutomationGalleryOpen} title={GetText('AutomaticSending_CreateModalTitle')} enablePaymentLink={enablePaymentLink}
                        onClose={() => setIsAutomationGalleryOpen(false)} onCreateBlank={() => setAutomationBlankRequestId(previous => previous + 1)} onUseTemplate={onUseOneFrontAutomationTemplate} />
                </>
            ),
        },
    ];

    return (
        <LayoutLeftMenu title={GetText('EmailTemplates_TemplateTitle')} menu={<LeftMenuSettings selected="emailTemplatesAndAutomation" />}>
            <Tabs defaultActiveKey="templates" items={tabItems} />
        </LayoutLeftMenu>
    );
};

export default AdminEmailTemplatesAndAutomation;
